import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../utils/api';
import './EventAttendance.css';

const EventAttendance = () => {
  const [form, setForm] = useState({
    supplierName: '',
    participantName: '',
    attend: '',
  });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [attendanceChoice, setAttendanceChoice] = useState('');
  const [csvData, setCsvData] = useState([]);
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [participantSuggestions, setParticipantSuggestions] = useState([]);
  const [showSupplierSuggestions, setShowSupplierSuggestions] = useState(false);
  const [showParticipantSuggestions, setShowParticipantSuggestions] = useState(false);

  useEffect(() => {
    // Show form after 5 seconds
    const timer = setTimeout(() => {
      setShowForm(true);
    }, 5000);

    // Load CSV data for suggestions
    loadCsvData();

    return () => clearTimeout(timer);
  }, []);

  const loadCsvData = async () => {
    try {
      const url = buildApiUrl('csv-data');
      console.log('Fetching CSV data from:', url);
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`Failed to fetch CSV data: ${res.status} ${res.statusText}`);
      }
      const data = await res.json();
      setCsvData(data);
    } catch (err) {
      console.error('Error loading CSV data:', err);
      // Don't show error to user, just log it - CSV data is for autocomplete suggestions
    }
  };

  const handleVideoEnd = () => {
    setShowForm(true);
  };

  const handleSupplierNameChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, supplierName: value }));
    setStatus(null);
    
    if (value.trim() === '') {
      setSupplierSuggestions([]);
      setShowSupplierSuggestions(false);
      return;
    }
    
    // Filter suppliers based on input
    const filtered = csvData.filter((row) => 
      row['SUPPLIER NAME'] && 
      row['SUPPLIER NAME'].toLowerCase().includes(value.toLowerCase())
    );
    
    const uniqueSuppliers = [...new Set(filtered.map(row => row['SUPPLIER NAME']))];
    setSupplierSuggestions(uniqueSuppliers.slice(0, 10));
    setShowSupplierSuggestions(true);
  };

  const handleParticipantNameChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, participantName: value }));
    setStatus(null);
    
    if (value.trim() === '') {
      setParticipantSuggestions([]);
      setShowParticipantSuggestions(false);
      setForm((prev) => ({ ...prev, supplierName: '' }));
      return;
    }
    
    // Filter participants based on input
    const filtered = csvData.filter((row) => 
      row['NAME  OF PARTICIPANTS'] && 
      row['NAME  OF PARTICIPANTS'].toLowerCase().includes(value.toLowerCase())
    );
    
    const uniqueParticipants = [...new Set(filtered.map(row => row['NAME  OF PARTICIPANTS']))];
    setParticipantSuggestions(uniqueParticipants.slice(0, 10));
    setShowParticipantSuggestions(true);
  };

  const selectSupplier = (supplier) => {
    setForm((prev) => ({ ...prev, supplierName: supplier }));
    setShowSupplierSuggestions(false);
    setSupplierSuggestions([]);
    
    // Find participants for this supplier
    const matchingParticipants = csvData.filter((row) => 
      row['SUPPLIER NAME'] === supplier
    ).map(row => row['NAME  OF PARTICIPANTS']);
    
    // If there's only one participant for this supplier, auto-fill it
    if (matchingParticipants.length === 1) {
      setForm((prev) => ({ ...prev, participantName: matchingParticipants[0] }));
    }
  };

  const selectParticipant = (participant) => {
    setForm((prev) => ({ ...prev, participantName: participant }));
    setShowParticipantSuggestions(false);
    setParticipantSuggestions([]);
    
    // Find the matching supplier for this participant
    const matchingRow = csvData.find((row) => 
      row['NAME  OF PARTICIPANTS'] === participant
    );
    
    if (matchingRow && matchingRow['SUPPLIER NAME']) {
      setForm((prev) => ({ ...prev, supplierName: matchingRow['SUPPLIER NAME'] }));
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus(null);
    
    // Add validation animation
    const formElement = e.target;
    if (!form.supplierName || !form.participantName || !form.attend) {
      setStatus({ type: 'error', message: 'Please fill all fields correctly.' });
      
      // Add shake animation to form
      formElement.classList.add('error');
      setTimeout(() => formElement.classList.remove('error'), 500);
      
      setLoading(false);
      return;
    }
    
    try {
      console.log('Submitting form data:', {
        supplierName: form.supplierName,
        participantName: form.participantName,
        attend: form.attend,
      });
      const submitUrl = buildApiUrl('submit-form');
      const res = await fetch(submitUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          supplierName: form.supplierName.trim(),
          participantName: form.participantName.trim(),
          attend: form.attend
        })
      });
      
      const data = await res.json();
      
      if (res.ok && data.success) {
        // Add success animation
        formElement.classList.add('success');
        setTimeout(() => formElement.classList.remove('success'), 600);
        
        // Set popup message based on attendance choice
        if (form.attend === 'Yes') {
          setPopupMessage("Thank you for your kind confirmation. We look forward to welcoming you at the event.");
          setAttendanceChoice('yes');
        } else {
          setPopupMessage("Thank you for your response. Sorry we won't have the pleasure of seeing you at the event");
          setAttendanceChoice('no');
        }
        setShowPopup(true);
        setForm({ supplierName: '', participantName: '', attend: '' });
      } else {
        setStatus({ type: 'error', message: data.error || 'Submission failed.' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Network or server error.' });
    }
    setLoading(false);
  };

  const closePopup = () => {
    setShowPopup(false);
    setPopupMessage('');
    setAttendanceChoice('');
  };

  return (
    <div>
      {/* Admin Link */}
      {/* <div style={{ 
        position: 'fixed', 
        top: '10px', 
        right: '10px', 
        zIndex: 1000,
        background: 'rgba(255,255,255,0.9)',
        padding: '5px 10px',
        borderRadius: '5px',
        fontSize: '12px'
      }}>
        <a href="/admin/login" style={{ color: '#666', textDecoration: 'none' }}>
          Admin Login
        </a>
      </div> */}

      <div className="event-bg">
        <div className="video-container" style={{ display: showForm ? 'none' : 'block' }}>
          <video autoPlay muted playsInline onEnded={handleVideoEnd}>
            <source src={process.env.PUBLIC_URL + '/mp.mp4'} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="event-container" style={{ display: showForm ? 'block' : 'none' }}>
          <img src={process.env.PUBLIC_URL + '/logo.jpg'} alt="Event Logo" className="event-logo" />
          <div className="event-details">
            <div className="event-venue">
              <b>Venue</b><br/>
              <span className="event-venue-name">Monarch Imperial</span><br/>
              <span className="event-venue-address">Sri Jayawardenepura Kotte</span>
            </div>
            <div className="event-date-block">
              <span className="event-date-label">Tuesday</span>
              <span className="event-date-day">8<sup>th</sup></span>
              <span className="event-date-month">July 2025</span>
            </div>
            <div className="event-time">From <b>7:00 PM</b> onwards</div>
          </div>
          <div className="event-attendance-title"><b>Event Attendance</b></div>
          <form className="event-form" onSubmit={handleSubmit} autoComplete="off">
            <label className="event-label">NAME OF PARTICIPANTS<span className="required">*</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name="participantName"
                placeholder="Enter participant name"
                className="event-input"
                value={form.participantName}
                onChange={handleParticipantNameChange}
                onFocus={() => setShowParticipantSuggestions(true)}
                onBlur={() => setTimeout(() => setShowParticipantSuggestions(false), 200)}
                required
                autoComplete="off"
              />
              {showParticipantSuggestions && participantSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  {participantSuggestions.map((participant, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        fontSize: '14px'
                      }}
                      onMouseDown={() => selectParticipant(participant)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {participant}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="event-label">COMPANY / SUPPLIER NAME<span className="required">*</span></label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name="supplierName"
                placeholder="Enter supplier name"
                className="event-input"
                value={form.supplierName}
                onChange={handleSupplierNameChange}
                onFocus={() => setShowSupplierSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSupplierSuggestions(false), 200)}
                required
                autoComplete="off"
              />
              {showSupplierSuggestions && supplierSuggestions.length > 0 && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  {supplierSuggestions.map((supplier, index) => (
                    <div
                      key={index}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid #eee',
                        fontSize: '14px'
                      }}
                      onMouseDown={() => selectSupplier(supplier)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {supplier}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label className="event-label">Event Participation<span className="required">*</span></label>
            <div className="event-radio-group">
              <label className="event-radio-label">
                <input
                  type="radio"
                  name="attend"
                  value="Yes"
                  checked={form.attend === 'Yes'}
                  onChange={handleChange}
                  required
                /> Yes
              </label>
              <label className="event-radio-label">
                <input
                  type="radio"
                  name="attend"
                  value="No"
                  checked={form.attend === 'No'}
                  onChange={handleChange}
                  required
                /> No
              </label>
            </div>
            <button
              type="submit"
              className="event-input"
              style={{ 
                marginTop: 16, 
                background: '#b00', 
                color: '#fff', 
                fontWeight: 600, 
                cursor: loading ? 'not-allowed' : 'pointer' 
              }}
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </form>
          {status && (
            <div style={{ marginTop: 16, color: status.type === 'success' ? 'green' : '#b00', fontWeight: 500, fontSize: '1em' }}>
              {status.message}
            </div>
          )}
        </div>
      </div>

      {/* Success Popup */}
      {showPopup && (
        <div className="popup-overlay" onClick={closePopup}>
           <div className={`popup-content ${attendanceChoice === 'yes' ? 'popup-yes' : 'popup-no'}`} onClick={(e) => e.stopPropagation()}>
            <div className="popup-message">{popupMessage}</div>
            <button className="popup-close-btn" onClick={closePopup}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventAttendance; 